import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
  Type,
  ComponentFactoryResolver,
  ChangeDetectorRef,
  OnInit
} from '@angular/core';
import { CustomHostDirective } from 'gung-list';
import { Subscription, Subject, of, switchMap, Observable, from, catchError, first } from 'rxjs';
import { ColmecWorkingOrderStepComponent } from '../steps/colmec-working-order-step/colmec-working-order-step.component';
import { ColmecWorkingOrderButtonsComponent } from '../colmec-working-order-buttons/colmec-working-order-buttons.component';
import { ColmecWorkingOrderStepIndicatorComponent } from '../colmec-working-order-step-indicator/colmec-working-order-step-indicator.component';
import { ColmecWorkingOrderConfigService } from '../../../services/colmec-working-order-config.service';
import { HttpClient } from '@angular/common/http';
import { AuthService, GungBase, GungModalService } from 'gung-standard';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColmecWorkingOrderTiresStepComponent } from '../steps/colmec-working-order-tires-step/colmec-working-order-tires-step.component';
import { ColmecWorkingOrderService } from '../../../services/colmec-working-order.service';

export interface ColmecWorkOrder extends GungBase {
  agreementId: string;
  workPerformedDate: Date;
  regNr: string;
  reasonForWork: string;
  tires: ColmecWorkOrderTire[];
  services: ColmecWorkOrderServiceModel[];
  extras: ColmecWorkOrderExtra[];
  workState?: string;
}

export interface ColmecWorkOrderTire extends GungBase {
  id: string;
  qty: number;
  name: string;
  position: string;
  tireAxle: string;
  supplierArticleNumber: string;
  supplierEAN?: string;
  price: ColmecTirePrice;
  errors?: { [s: string]: string };
}

export interface ColmecWorkOrderServiceModel extends GungBase {
  id: string;
  qty: number;
  name: string;
  price: ColmecTirePrice;
}

export interface ColmecTirePrice extends GungBase {
  value: number;
  currency: string;
  discountPercentage?: number;
  valueNoDiscount?: number;
}

export interface ColmecWorkOrderExtra extends GungBase {
  description: string;
  price: string;
}

@Component({
    selector: 'colmec-working-order-wrapper',
    templateUrl: './colmec-working-order-wrapper.component.html',
    styleUrls: ['./colmec-working-order-wrapper.component.scss'],
    standalone: false
})
export class ColmecWorkingOrderWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  public error?: any;

  @ViewChild(CustomHostDirective)
  protected viewChild: CustomHostDirective;

  @ViewChild('indicator', { read: ViewContainerRef })
  protected indicator: ViewContainerRef;

  @ViewChild('btns', { read: ViewContainerRef })
  protected btns: ViewContainerRef;

  protected currentIndex = 0;
  protected visibleIndex = 0;

  protected steps: Type<ColmecWorkingOrderStepComponent>[];

  protected stepIndexVisible: boolean[];

  public get isSubmitting(): boolean {
    return this.submitting;
  }

  public get showLoading(): boolean {
    return this.submitting || !this.stepIndexVisible || !this.stepIndexVisible[this.currentIndex];
  }

  protected submitting = false;

  private currentStepDoneSubscription?: Subscription;
  private currentStepErrorSubscription?: Subscription;

  protected workingOrder: ColmecWorkOrder;

  protected nextStepSubject = new Subject<void | any>();
  protected previousStepSubject = new Subject<void | any>();

  protected renderedButtons: ColmecWorkingOrderButtonsComponent;

  protected renderedIndicator: ColmecWorkingOrderStepIndicatorComponent;

  get index(): number {
    return this.currentIndex;
  }

  get nextBtnText(): string {
    return 'next';
  }

  get prevBtnText(): string {
    return 'previous';
  }

  public isSubmitted: boolean = false;
  public hasErrors: boolean = false;

  protected isEditWorkorder: boolean = false;
  protected isAdmin: boolean = false;
  protected isSales: boolean = false;

  constructor(
    protected workingOrderConfig: ColmecWorkingOrderConfigService,
    protected componentFactoryResolver: ComponentFactoryResolver,
    protected changeDetectorRef: ChangeDetectorRef,
    protected http: HttpClient,
    protected modalService: GungModalService,
    protected router: Router,
    protected translateService: TranslateService,
    protected route: ActivatedRoute,
    protected colmecWorkingOrderService: ColmecWorkingOrderService,
    protected authService: AuthService
  ) { }

  // EVENT HANDLERS
  nextBtnClicked(nextInfo?: any) {
    if (nextInfo) {
      this.nextStepSubject.next(nextInfo);
    } else {
      this.nextStepSubject.next(undefined);
    }
  }

  // EVENT HANDLERS
  previousBtnClickedButtons(nextInfo?: any) {
    if (nextInfo) {
      this.previousStepSubject.next(nextInfo);
    } else {
      this.previousStepSubject.next(undefined);
    }
  }

  previousBtnClicked() {
    const slicedSteps = this.steps.slice(0, this.currentIndex);
    const filteredSteps = slicedSteps.filter(step => step.prototype.isVisible());
    const lastStep = filteredSteps[filteredSteps.length - 1];
    this.currentIndex = this.steps.indexOf(lastStep);
    this.visibleIndex--;
    this.nextStepSubject.complete();
    this.previousStepSubject.complete();
    this.renderCurrentStep();
    this.renderDynamicComponents();
  }

  ngOnInit(): void {
    if (this.route.snapshot.params.id) {
      this.isEditWorkorder = true;
    }

    this.authService.getHasRoles('ADMIN').pipe(first()).subscribe(isAdmin => this.isAdmin = isAdmin);
    this.authService.getHasRoles('SALES').pipe(first()).subscribe(isSales => this.isSales = isSales);

    window.addEventListener('beforeunload', e => {
      if (true) {
        const confirmationMessage = 'o/';
        e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+
        return confirmationMessage; // Gecko, WebKit, Chrome <34
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.workingOrderConfig.getWorkingOrderSteps().subscribe(steps => {
      this.steps = steps;
      this.stepIndexVisible = this.steps.map(s => s.prototype.isVisible());
      this.renderDynamicComponents();
      this.renderCurrentStep();
    });
  }

  protected renderDynamicComponents() {
    // render the indicator
    const indicatorFactory = this.componentFactoryResolver.resolveComponentFactory(
      ColmecWorkingOrderStepIndicatorComponent
    );
    this.indicator.clear();
    this.renderedIndicator = this.indicator.createComponent(indicatorFactory).instance;
    this.renderedIndicator.currentIndex = this.visibleIndex;
    this.renderedIndicator.stepIndexVisible = this.stepIndexVisible;
    this.renderedIndicator.steps = this.steps;

    // render the buttons
    const buttonsFactory = this.componentFactoryResolver.resolveComponentFactory(ColmecWorkingOrderButtonsComponent);
    this.btns.clear();
    this.renderedButtons = this.btns.createComponent(buttonsFactory).instance;
    this.renderedButtons.allSteps = this.steps.filter(step => step.prototype.isVisible());
    this.renderedButtons.currentStep = this.steps[this.currentIndex];
    this.renderedButtons.nextClicked.subscribe(_ => this.nextBtnClicked(_));
    this.renderedButtons.prevClicked.subscribe(_ => this.previousBtnClickedButtons());
    this.renderedButtons.updateBtnTexts(this.workingOrder);

    this.changeDetectorRef.detectChanges();
  }

  protected unsubscribe() {
    if (this.currentStepDoneSubscription) {
      this.currentStepDoneSubscription.unsubscribe();
    }
    if (this.currentStepErrorSubscription) {
      this.currentStepErrorSubscription.unsubscribe();
    }
    if (this.nextStepSubject) {
      this.nextStepSubject.complete();
    }
    if (this.previousStepSubject) {
      this.previousStepSubject.complete();
    }
  }

  protected nextStep() {
    this.currentIndex++;
    if (this.currentIndex === this.steps.length) {
      // SUBMIT //
      this.submitWorkingOrder();
      return;
    }
    // render the next component
    this.renderCurrentStep();
    if (this.steps[this.currentIndex].prototype.isVisible()) {
      this.updateDynamicComponents();
    }
  }

  private updateDynamicComponents() {
    // TODO update the buttons and the stepper
    this.changeDetectorRef.detectChanges();
    this.renderDynamicComponents();
  }

  protected displayError(workingOrderError: any) {
    this.error = workingOrderError;
  }

  protected renderCurrentStep(): void {
    this.unsubscribe();

    const componentToRender: Type<ColmecWorkingOrderStepComponent> = this.steps[this.currentIndex];
    const factory = this.componentFactoryResolver.resolveComponentFactory(componentToRender);
    const containerRef = this.viewChild.viewContainerRef;
    containerRef.clear();
    const componentRef = containerRef.createComponent(factory);
    const typedComponent = componentRef.instance as ColmecWorkingOrderStepComponent;
    // TODO freeze this object ?
    // set up the inputs to the step

    if (this.workingOrder !== undefined) {
      typedComponent.workingOrder = JSON.parse(JSON.stringify(this.workingOrder));
    } else {
      typedComponent.workingOrder = this.workingOrder;
    }
    this.nextStepSubject = new Subject<void | any>();
    typedComponent.onNextBtnClicked = this.nextStepSubject.asObservable();
    this.previousStepSubject = new Subject<void | any>();
    typedComponent.onPreviousBtnClicked = this.previousStepSubject.asObservable();

    // subscribe to the outputs of the step
    typedComponent.stepDone.subscribe(newWorkingOrder => {
      if (typedComponent.isVisible()) {
        this.visibleIndex++;
      }
      this.nextStepSubject.complete();
      this.workingOrder = newWorkingOrder;
      this.nextStep();
    });
    // subscribe to the outputs of the step
    typedComponent.stepPrevious.subscribe(newWorkingOrder => {
      this.previousStepSubject.complete();
      this.workingOrder = newWorkingOrder;
      this.previousBtnClicked();
    });

    typedComponent.stepError.subscribe(err => {
      this.displayError(err);
    });

    // trigger lifecycle hooks on the step
    this.changeDetectorRef.detectChanges();
    if (!typedComponent.isVisible()) {
      // automatically trigger next if the step is not visible
      this.nextStepSubject.next(undefined);
    }
  }

  protected submitWorkingOrder() {
    if (this.isEditWorkorder) {
      this.colmecWorkingOrderService
        .updateWorkingOrder(this.workingOrder, this.isAdmin || this.isSales)
        .pipe(
          first(),
          catchError(_ => of(undefined))
        )
        .subscribe(result => {
          console.log('Submit working order result: ', result);
          this.isSubmitted = true;
          if (!!result?.error) {
            this.hasErrors = true;
            this.error = result.error;
          } else {
            this.hasErrors = false;
          }
        });
    } else {
      this.workingOrderConfig
        .getSubmitUrl()
        .pipe(
          switchMap(url => this.http.post<any>(url, this.workingOrder)),
          catchError(_ => {
            return of({
              error: this.translateService.instant('WORK_ORDER_GENERIC_ERROR')
            });
          })
        )
        .subscribe(result => {
          console.log('Submit working order result: ', result);
          this.isSubmitted = true;
          if (!!result?.error) {
            this.hasErrors = true;
            this.error = result.error;
          } else {
            this.hasErrors = false;
          }
        });
    }
  }

  public canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (this.router.getCurrentNavigation().trigger !== 'popstate') {
      return true;
    }
    return from(
      this.modalService.openConfirmYesNoModal('EXIT_WORKING_ORDER_POPUP', 'EXIT_WORKING_ORDER_POPUP_DESCRIPTION')
    );
  }

  public goBackToTireStep(): void {
    this.isSubmitted = false;
    this.hasErrors = false;
    this.error = undefined;
    this.currentIndex = this.steps.indexOf(ColmecWorkingOrderTiresStepComponent);
    this.visibleIndex = this.steps.indexOf(ColmecWorkingOrderTiresStepComponent) - 1;
    this.nextStepSubject.complete();
    this.previousStepSubject.complete();
    this.renderCurrentStep();
    this.renderDynamicComponents();
  }

  protected createWorkOrderIgnoreTirePolicy(): void {
    this.workingOrderConfig
      .getSubmitUrlIgnoreTirePolicy()
      .pipe(
        switchMap(url => this.http.post<any>(url, this.workingOrder)),
        catchError(_ => {
          return of({
            error: this.translateService.instant('WORK_ORDER_GENERIC_ERROR')
          });
        })
      )
      .subscribe(result => {
        console.log('Submit working order result: ', result);
        this.isSubmitted = true;
        if (!!result?.error) {
          this.hasErrors = true;
          this.error = result.error;
        } else {
          this.hasErrors = false;
        }
      });
  }
}
